import { ApiHttpClient } from "../../common/http/api-http-client";
import { ResourceBaseService } from "../../common/resource-base/resource-base.service";
import { OptionType } from "@/types/OptionType";
import { IRequest } from "@/interfaces/IRequest";
import { merge } from "lodash";
import { PurchaseOrderDto } from "@/core/features/purchase-orders/purchase-order.dto";
import { CreatePurchaseOrderDto } from "@/core/features/purchase-orders/create-purchase-order.dto";
import { UpdatePurchaseOrderDto } from "@/core/features/purchase-orders/update-purchase-order.dto";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { PurchaseOrdersTotalDto } from "@/core/features/purchase-orders/purchase-orders-total.dto";
import { PurchaseOrderStatusEnum } from "@altertec_gparn/lib";
import { ChangePurchaseOrderStatusDto } from "@/core/features/purchase-orders/change-purchase-order-status.dto";
import { UpdatePurchaseApprovalConfigurationDto } from "@/core/features/purchase-orders/update-purchase-approval-configuration.dto";
import { PurchaseApprovalConfigurationDto } from "@/core/features/purchase-orders/purchase-approval-configuration.dto";
import { CreateFileDto } from "@/core/features/file/create-file.dto";
import { DeleteFileDto } from "@/core/features/file/delete-file.dto";
import { ExtractPurchaseOrderLinesResultDto } from "./extract-purchase-order-lines-result.dto";
import { PurchaseOrderRecordDto } from "@/core/features/purchase-orders/purchase-order-record.dto";
import { applyOptionLoaderPagination } from "@/core/common/presenters/optionLoader";

export class PurchaseOrdersResource extends ResourceBaseService<
  PurchaseOrderDto,
  CreatePurchaseOrderDto,
  UpdatePurchaseOrderDto,
  PurchaseOrderRecordDto
> {
  protected EntityDto = PurchaseOrderDto;
  protected CreateEntityDto = CreatePurchaseOrderDto;
  protected UpdateEntityDto = UpdatePurchaseOrderDto;
  protected RecordEntityDto = PurchaseOrderRecordDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/purchase-orders");
  }

  async optionLoader(inputValue: string, customRequest: IRequest = {}): Promise<OptionType[]> {
    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "code"] } : {};
    request.sortBy = "createdAt";
    const { data } = await this.findAll(applyOptionLoaderPagination(merge(customRequest, request)));
    return data.map((value) => value.mapEntityToOptionType([value.code, value.supplier.toString()]));
  }

  async getTotal(request: IRequest): Promise<PurchaseOrdersTotalDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/total",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => plainToInstance(PurchaseOrdersTotalDto, result));
  }

  async isDeletable(id: string): Promise<boolean> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + `/${id}/is-deletable`,
          method: "GET",
          auth: true,
        })
      )
      .then((result) => result === "true");
  }

  changeStatus(id: string, status: PurchaseOrderStatusEnum): Promise<PurchaseOrderDto & { jobId?: string }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/change-status",
        method: "PATCH",
        auth: true,
        body: plainToInstance(ChangePurchaseOrderStatusDto, { status: status, id: id }),
      })
    );
  }

  createManyAttachments(id: string, files: CreateFileDto[]): Promise<PurchaseOrderDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "POST",
        auth: true,
        body: { bulk: files },
      })
    );
  }

  deleteAttachment(id: string, deleteFile: DeleteFileDto): Promise<PurchaseOrderDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "DELETE",
        auth: true,
        body: deleteFile,
      })
    );
  }

  async copy(id: string): Promise<PurchaseOrderDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/" + id + "/copy",
          method: "POST",
          auth: true,
        })
      )
      .then((result) => plainToInstance(PurchaseOrderDto, result));
  }

  saveApprovalConfiguration(body: UpdatePurchaseApprovalConfigurationDto): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/approval-configuration",
        method: "POST",
        auth: true,
        body: plainToInstance(UpdatePurchaseApprovalConfigurationDto, body),
      })
    );
  }

  async getApprovalConfiguration(): Promise<PurchaseApprovalConfigurationDto[]> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/approval-configuration",
        method: "GET",
        auth: true,
      })
    );
    return plainToInstance(PurchaseApprovalConfigurationDto, result) as unknown as PurchaseApprovalConfigurationDto[];
  }

  async importLines(purchaseOrderId: string, file: File): Promise<{ jobId: string }> {
    const formData = new FormData();
    formData.append("file", file);

    return this.apiHttpClient.request<FormData, { jobId: string }>(
      HttpRequest.create<FormData>({
        url: this.resourceUrl + `/${purchaseOrderId}/import-lines`,
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );
  }

  async extractLinesFromDocument(file: File, supplierId?: string): Promise<ExtractPurchaseOrderLinesResultDto> {
    const formData = new FormData();
    formData.append("file", file);

    if (supplierId) {
      formData.append("supplierId", supplierId);
    }

    return this.apiHttpClient
      .request<FormData, ExtractPurchaseOrderLinesResultDto>(
        HttpRequest.create<FormData>({
          url: this.resourceUrl + "/extract-lines-from-document",
          method: "POST",
          auth: true,
          body: formData,
          type: "form-data",
        })
      )
      .then((result) => plainToInstance(ExtractPurchaseOrderLinesResultDto, result));
  }

  async downloadImportTemplate(): Promise<void> {
    return this.downloadExcel(null, "/import/template", "plantilla-lineas-pedido");
  }

  async exportLinesExcel(purchaseOrderId: string, purchaseOrderCode: string): Promise<void> {
    return this.downloadExcel(null, `/${purchaseOrderId}/lines/excel`, `lineas-pedido-${purchaseOrderCode}`);
  }

  archive(id: string): Promise<PurchaseOrderDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/archive",
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }

  unarchive(id: string): Promise<PurchaseOrderDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/unarchive",
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }

  retrySapSync(id: string): Promise<{ jobId?: string }> {
    return this.apiHttpClient.request<null, { jobId?: string }>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/retry-sap-sync",
        method: "POST",
        auth: true,
      })
    );
  }
}
